import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import {
  ArrowDownwardOutlined,
  ArrowUpwardOutlined,
  Inventory2Outlined,
  SearchOutlined,
  TimelineOutlined,
  TuneOutlined,
} from "@mui/icons-material";

import DatePick from "../../Components/DatePick";
import CustomSnackBar from "../../Components/CustomSnackBar";
import MainText from "../../Components/MainText";
import { useAuth } from "../../context/AuthContext";
import { inventoryService, productsService } from "../../services";
import ActiveGeneralInventoryAlert from "./components/ActiveGeneralInventoryAlert";

const toArray = (value) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.results)) return value.results;
  return [];
};

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatCompactQty = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return "0";
  if (Number.isInteger(parsed)) return String(parsed);
  return parsed.toFixed(3).replace(/\.?0+$/, "");
};

function useDebouncedValue(value, delay = 300) {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const timeout = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timeout);
  }, [delay, value]);

  return debounced;
}

const toDateInput = (value) => {
  if (!value) return undefined;
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return undefined;
  return date.toISOString().slice(0, 10);
};

const readableProductQty = (qtyBase, presentations, withSign = false) => {
  const safeQtyBase = toNumber(qtyBase);
  const sign = safeQtyBase < 0 ? "-" : withSign && safeQtyBase > 0 ? "+" : "";
  const absoluteQty = Math.abs(safeQtyBase);
  const sorted = [...(presentations || [])]
    .filter((row) => toNumber(row.qty_in_base) > 0)
    .sort((a, b) => toNumber(a.qty_in_base) - toNumber(b.qty_in_base));

  if (sorted.length === 0) {
    return `${sign}${formatCompactQty(absoluteQty)} unidades`;
  }

  const minPresentation = sorted[0];
  const maxPresentation = sorted[sorted.length - 1];
  const minQtyInBase = toNumber(minPresentation.qty_in_base);
  const maxQtyInBase = toNumber(maxPresentation.qty_in_base);

  if (absoluteQty <= 0) {
    return `0 ${minPresentation.presentation_name || "unidades"}`;
  }

  if (maxQtyInBase <= minQtyInBase) {
    return `${sign}${formatCompactQty(absoluteQty / minQtyInBase)} ${minPresentation.presentation_name || "unidades"}`;
  }

  const maxUnits = Math.floor(absoluteQty / maxQtyInBase);
  const remainderBase = absoluteQty - maxUnits * maxQtyInBase;
  const minUnits = remainderBase / minQtyInBase;
  const parts = [];
  if (maxUnits > 0) {
    parts.push(`${formatCompactQty(maxUnits)} ${maxPresentation.presentation_name || "unidades"}`);
  }
  if (minUnits > 0) {
    parts.push(`${formatCompactQty(minUnits)} ${minPresentation.presentation_name || "unidades"}`);
  }
  return `${sign}${parts.join(" y ")}`;
};

const movementVisuals = {
  IN: {
    color: "success.main",
    icon: <ArrowUpwardOutlined fontSize="small" />,
  },
  OUT: {
    color: "error.main",
    icon: <ArrowDownwardOutlined fontSize="small" />,
  },
  ADJUSTMENT: {
    color: "warning.main",
    icon: <TuneOutlined fontSize="small" />,
  },
};

const movementTypeLabels = {
  IN: "Entrada",
  OUT: "Salida",
  ADJUSTMENT: "Ajuste",
};

function KpiCard({ label, value, color = "primary.main" }) {
  return (
    <Card variant="outlined">
      <CardContent sx={{ py: 1.3, "&:last-child": { pb: 1.3 } }}>
        <Typography variant="h6" sx={{ fontWeight: 800, color }}>
          {value}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {label}
        </Typography>
      </CardContent>
    </Card>
  );
}

function StockMovementsPage() {
  const { currentBranch, isSuperuser, effectivePerms = [] } = useAuth();
  const branchId = currentBranch?.id || null;
  const canView = Boolean(isSuperuser || effectivePerms.includes("inventory_movement_view"));

  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedProductDetail, setSelectedProductDetail] = useState(null);
  const [stockRows, setStockRows] = useState([]);
  const [movements, setMovements] = useState([]);
  const [loadingMovements, setLoadingMovements] = useState(false);
  const [dateFrom, setDateFrom] = useState(() => {
    const next = new Date();
    next.setDate(next.getDate() - 6);
    return next;
  });
  const [dateTo, setDateTo] = useState(() => new Date());
  const [periodPreset, setPeriodPreset] = useState("WEEK");
  const [viewMode, setViewMode] = useState("OPERATIVE");

  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMsg, setSnackMsg] = useState("");
  const [snackSev, setSnackSev] = useState("info");

  const debouncedSearch = useDebouncedValue(searchTerm, 300);

  const showSnack = (message, severity = "info") => {
    setSnackMsg(message);
    setSnackSev(severity);
    setSnackOpen(true);
  };

  useEffect(() => {
    if (!canView || debouncedSearch.trim().length < 2) {
      setSearchResults([]);
      return;
    }

    let cancelled = false;
    (async () => {
      setSearchLoading(true);
      try {
        const data = await productsService.list({ search: debouncedSearch.trim(), active: "true" });
        if (!cancelled) {
          setSearchResults(toArray(data));
        }
      } catch {
        if (!cancelled) {
          setSearchResults([]);
        }
      } finally {
        if (!cancelled) {
          setSearchLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [canView, debouncedSearch]);

  const loadSelectedProduct = useCallback(
    async (product) => {
      if (!branchId || !product?.id) return;
      setSelectedProduct(product);
      try {
        const [detail, stocks] = await Promise.all([
          productsService.get(product.id),
          inventoryService.listStocks({ branch_id: branchId, product_id: product.id }),
        ]);
        setSelectedProductDetail(detail || null);
        setStockRows(toArray(stocks));
      } catch {
        setSelectedProductDetail(null);
        setStockRows([]);
        showSnack("No se pudo cargar el producto seleccionado.", "error");
      }
    },
    [branchId]
  );

  const loadMovements = useCallback(async () => {
    if (!branchId || !selectedProduct?.id || !canView) {
      setMovements([]);
      return;
    }

    setLoadingMovements(true);
    try {
      const data = await inventoryService.listStockMovements({
        branch_id: branchId,
        product_id: selectedProduct.id,
        date_from: toDateInput(dateFrom),
        date_to: toDateInput(dateTo),
      });
      setMovements(toArray(data));
    } catch {
      setMovements([]);
      showSnack("No se pudieron cargar los movimientos.", "error");
    } finally {
      setLoadingMovements(false);
    }
  }, [branchId, canView, dateFrom, dateTo, selectedProduct]);

  useEffect(() => {
    loadMovements();
  }, [loadMovements]);

  const activePresentations = useMemo(() => {
    const detailPresentations = toArray(selectedProductDetail?.presentations).filter((row) => row?.is_active);
    return detailPresentations.map((presentation) => ({
      id: presentation.id,
      presentation_name: presentation?.product_format?.name || "Presentacion",
      qty_in_base: toNumber(presentation?.qty_in_base),
    }));
  }, [selectedProductDetail]);

  const currentStockBase = useMemo(
    () => stockRows.reduce((total, row) => total + toNumber(row?.qty_base), 0),
    [stockRows]
  );

  const presentationBadges = useMemo(
    () => [
      {
        id: "stock",
        label: `Existencia: ${readableProductQty(currentStockBase, activePresentations)}`,
      },
    ],
    [activePresentations, currentStockBase]
  );

  const decoratedMovements = useMemo(() => {
    const runningStock = {};
    stockRows.forEach((row) => {
      const key = String(row?.product_id || selectedProduct?.id || "");
      runningStock[key] = toNumber(runningStock[key]) + toNumber(row?.qty_base);
    });

    return movements.map((row) => {
      const key = String(row?.product_id || selectedProduct?.id || "");
      const stockAfter = toNumber(runningStock[key]);
      runningStock[key] = stockAfter - toNumber(row?.qty_base_delta);
      return {
        ...row,
        stockAfter,
      };
    });
  }, [movements, selectedProduct, stockRows]);

  const periodSummary = useMemo(() => {
    let totalInBase = 0;
    let totalOutBase = 0;

    decoratedMovements.forEach((row) => {
      const delta = toNumber(row?.qty_base_delta);
      if (delta > 0) totalInBase += delta;
      if (delta < 0) totalOutBase += Math.abs(delta);
    });

    const balanceBase = totalInBase - totalOutBase;
    return {
      totalIn: readableProductQty(totalInBase, activePresentations),
      totalOut: readableProductQty(totalOutBase, activePresentations),
      balance: readableProductQty(balanceBase, activePresentations, true),
      balanceColor: balanceBase >= 0 ? "success.main" : "error.main",
    };
  }, [activePresentations, decoratedMovements]);

  const applyPreset = (preset) => {
    const end = new Date();
    const start = new Date();
    if (preset === "TODAY") {
      setDateFrom(end);
      setDateTo(end);
    }
    if (preset === "WEEK") {
      start.setDate(end.getDate() - 6);
      setDateFrom(start);
      setDateTo(end);
    }
    if (preset === "MONTH") {
      start.setMonth(end.getMonth() - 1);
      setDateFrom(start);
      setDateTo(end);
    }
    setPeriodPreset(preset);
  };

  if (!canView) {
    return (
      <Box sx={{ py: 4 }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          Sin permiso
        </Typography>
        <Typography variant="body2" color="text.secondary">
          No tienes permisos para ver movimientos de inventario.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: 1.5 }}>
      <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={1.2}>
        <MainText
          title="Movimientos de inventario"
          subtitle="Consulta historica por producto"
          icon={TimelineOutlined}
        />
        <TextField
          size="small"
          label="Buscar producto"
          placeholder="Nombre o codigo"
          value={searchTerm}
          onChange={(event) => setSearchTerm(event.target.value)}
          sx={{ minWidth: { xs: "100%", md: 300 } }}
        />
      </Stack>

      <ActiveGeneralInventoryAlert />

      {searchTerm.trim().length >= 2 && !selectedProduct && (
        <Paper variant="outlined" sx={{ p: 1, maxHeight: 180, overflowY: "auto" }}>
          {searchLoading ? (
            <Typography variant="body2" color="text.secondary">
              Buscando...
            </Typography>
          ) : searchResults.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              Sin resultados.
            </Typography>
          ) : (
            <Stack spacing={0.65}>
              {searchResults.map((row) => (
                <Stack
                  key={row.id}
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{ p: 0.85, borderRadius: 1, bgcolor: "background.default" }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>
                      {row.description || "Producto"}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" noWrap>
                      {row.code || "-"}
                    </Typography>
                  </Box>
                  <Button size="small" variant="outlined" onClick={() => loadSelectedProduct(row)}>
                    Ver movimientos
                  </Button>
                </Stack>
              ))}
            </Stack>
          )}
        </Paper>
      )}

      {!selectedProduct ? (
        <Paper
          variant="outlined"
          sx={{
            minHeight: 420,
            display: "grid",
            placeItems: "center",
            textAlign: "center",
            px: 2,
          }}
        >
          <Stack spacing={1.1} alignItems="center" sx={{ maxWidth: 320 }}>
            <Box
              sx={{
                width: 72,
                height: 72,
                borderRadius: "50%",
                display: "grid",
                placeItems: "center",
                bgcolor: "action.hover",
                color: "text.secondary",
              }}
            >
              <Inventory2Outlined fontSize="large" />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Busca un producto para ver sus movimientos
            </Typography>
            <Typography variant="body2" color="text.secondary">
              La vista no carga movimientos hasta que selecciones un producto.
            </Typography>
          </Stack>
        </Paper>
      ) : (
        <>
          <Paper variant="outlined" sx={{ p: 1.4 }}>
            <Stack spacing={1.1}>
              <Stack
                direction={{ xs: "column", md: "row" }}
                justifyContent="space-between"
                spacing={1}
                alignItems={{ md: "center" }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                    {selectedProduct.description || "Producto"}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {selectedProduct.code || "Sin codigo"}
                  </Typography>
                </Box>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<SearchOutlined fontSize="small" />}
                  onClick={() => {
                    setSelectedProduct(null);
                    setSelectedProductDetail(null);
                    setStockRows([]);
                    setMovements([]);
                  }}
                >
                  Cambiar producto
                </Button>
              </Stack>

              <Stack direction="row" spacing={0.8} flexWrap="wrap" useFlexGap>
                {presentationBadges.length === 0 ? (
                  <Chip size="small" label="Sin presentaciones activas" />
                ) : (
                  presentationBadges.map((badge) => (
                    <Chip key={badge.id} size="small" color="primary" variant="outlined" label={badge.label} />
                  ))
                )}
              </Stack>
            </Stack>
          </Paper>

          <Stack direction={{ xs: "column", md: "row" }} spacing={1} alignItems={{ md: "center" }}>
            <Stack direction="row" spacing={1}>
              <Button
                variant={periodPreset === "TODAY" ? "contained" : "outlined"}
                onClick={() => applyPreset("TODAY")}
              >
                Hoy
              </Button>
              <Button
                variant={periodPreset === "WEEK" ? "contained" : "outlined"}
                onClick={() => applyPreset("WEEK")}
              >
                Ultima semana
              </Button>
              <Button
                variant={periodPreset === "MONTH" ? "contained" : "outlined"}
                onClick={() => applyPreset("MONTH")}
              >
                Ultimo mes
              </Button>
            </Stack>
          <Stack direction={{ xs: "column", md: "row" }} spacing={1}>
            <DatePick
              name="date_from"
                label="Desde"
                size="small"
                allowHistoricalDates
                allowFutureDates={false}
                value={dateFrom}
                onChange={(_, nextDate) => {
                  setPeriodPreset("CUSTOM");
                  setDateFrom(nextDate);
                }}
              />
              <DatePick
                name="date_to"
                label="Hasta"
                size="small"
                allowHistoricalDates
                allowFutureDates={false}
                value={dateTo}
                onChange={(_, nextDate) => {
                  setPeriodPreset("CUSTOM");
                  setDateTo(nextDate);
                }}
              />
            </Stack>
            <Paper
              variant="outlined"
              sx={{
                p: 0.35,
                borderRadius: 2.5,
                bgcolor: "background.paper",
                alignSelf: { xs: "stretch", md: "center" },
              }}
            >
              <ToggleButtonGroup
                exclusive
                size="small"
                value={viewMode}
                onChange={(_, nextView) => {
                  if (nextView) setViewMode(nextView);
                }}
                sx={{
                  "& .MuiToggleButton-root": {
                    border: 0,
                    px: 1.4,
                    py: 0.45,
                    fontSize: "0.74rem",
                    fontWeight: 700,
                    color: "text.secondary",
                    textTransform: "none",
                    borderRadius: 2,
                  },
                  "& .Mui-selected": {
                    bgcolor: "action.selected",
                    color: "text.primary",
                  },
                }}
              >
                <ToggleButton value="OPERATIVE">Operativa</ToggleButton>
                <ToggleButton value="KARDEX">Kardex</ToggleButton>
              </ToggleButtonGroup>
            </Paper>
          </Stack>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0, 1fr))" },
              gap: 1.2,
            }}
          >
            <KpiCard label="Total entradas" value={periodSummary.totalIn} color="success.main" />
            <KpiCard label="Total salidas" value={periodSummary.totalOut} color="error.main" />
            <KpiCard label="Balance neto" value={periodSummary.balance} color={periodSummary.balanceColor} />
          </Box>

          <Paper variant="outlined" sx={{ p: 1.4 }}>
            {loadingMovements ? (
              <Typography variant="body2" color="text.secondary">
                Cargando movimientos...
              </Typography>
            ) : decoratedMovements.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No hay movimientos para el periodo seleccionado.
              </Typography>
            ) : viewMode === "KARDEX" ? (
              <TableContainer>
                <Table
                  size="small"
                  stickyHeader
                  sx={{
                    "& .MuiTableCell-root": {
                      px: 1,
                      py: 0.8,
                    },
                    "& .MuiTableHead-root .MuiTableCell-root": {
                      fontSize: "0.74rem",
                      fontWeight: 700,
                      bgcolor: "background.paper",
                    },
                    "& .MuiTableBody-root .MuiTableCell-root": {
                      fontSize: "0.78rem",
                    },
                  }}
                >
                  <TableHead>
                    <TableRow>
                      <TableCell>Fecha</TableCell>
                      <TableCell>Tipo</TableCell>
                      <TableCell>Referencia</TableCell>
                      <TableCell>Formato usado</TableCell>
                      <TableCell align="right">Entrada</TableCell>
                      <TableCell align="right">Salida</TableCell>
                      <TableCell align="right">Saldo</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {decoratedMovements.map((row) => {
                      const delta = toNumber(row?.qty_base_delta);
                      const visuals = movementVisuals[row?.movement_type] || movementVisuals.ADJUSTMENT;
                      const typeLabel = movementTypeLabels[row?.movement_type] || "Movimiento";

                      return (
                        <TableRow key={row.id} hover>
                          <TableCell>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.72rem" }}>
                              {new Date(row?.created_at).toLocaleString()}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              variant="outlined"
                              icon={visuals.icon}
                              label={typeLabel}
                              sx={{
                                height: 22,
                                color: visuals.color,
                                borderColor: "divider",
                                "& .MuiChip-label": { px: 0.9, fontSize: "0.7rem", fontWeight: 700 },
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontSize: "0.78rem" }}>
                              {row?.ref || "Sin referencia"}
                            </Typography>
                          </TableCell>
                          <TableCell>{row?.presentation_name || "Formato"}</TableCell>
                          <TableCell align="right" sx={{ color: delta > 0 ? "success.main" : "text.secondary", fontWeight: 700 }}>
                            {delta > 0
                              ? readableProductQty(delta, activePresentations)
                              : "-"}
                          </TableCell>
                          <TableCell align="right" sx={{ color: delta < 0 ? "error.main" : "text.secondary", fontWeight: 700 }}>
                            {delta < 0
                              ? readableProductQty(Math.abs(delta), activePresentations)
                              : "-"}
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700 }}>
                            {readableProductQty(row?.stockAfter, activePresentations)}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Stack spacing={0}>
                {decoratedMovements.map((row, index) => {
                  const visuals = movementVisuals[row?.movement_type] || movementVisuals.ADJUSTMENT;
                  return (
                    <React.Fragment key={row.id}>
                      <Stack direction="row" spacing={1.2} sx={{ py: 1.1 }}>
                        <Box
                          sx={{
                            width: 38,
                            height: 38,
                            borderRadius: "50%",
                            display: "grid",
                            placeItems: "center",
                            bgcolor: "action.hover",
                            color: visuals.color,
                            flexShrink: 0,
                          }}
                        >
                          {visuals.icon}
                        </Box>
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Typography variant="caption" color="text.secondary">
                            {new Date(row?.created_at).toLocaleString()}
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            {readableProductQty(row?.qty_base_delta, activePresentations, true)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            {row?.ref || "Sin referencia"}
                          </Typography>
                        </Box>
                        <Chip
                          size="small"
                          variant="outlined"
                          label={`Stock tras movimiento: ${readableProductQty(row?.stockAfter, activePresentations)}`}
                        />
                      </Stack>
                      {index < decoratedMovements.length - 1 && <Divider />}
                    </React.Fragment>
                  );
                })}
              </Stack>
            )}
          </Paper>
        </>
      )}

      <CustomSnackBar
        open={snackOpen}
        setOpen={setSnackOpen}
        message={snackMsg}
        severity={snackSev}
      />
    </Box>
  );
}

export default StockMovementsPage;
